<template>
  <div class="max-w-lg w-full mx-auto">

    <!-- Warn users of skipping selfie modal -->
    <div v-if="isSkipSelfieModalVisible" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg mx-5 md:w-1/3">
        <div class="flex items-center">
          <!-- <span class="text-3xl pr-4"></span> -->
          <svg class="mr-4 fill-current text-red-600" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"><path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-1.5 6h3l-1 8h-1l-1-8zm1.5 12.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/></svg>
          <h2 class="text-xl md:text-2xl font-bold text-red-600 leading-tight">Are you sure you want to skip?</h2>
        </div>
        <p class="mt-4 text-lg">
          <span v-if="isHeadshot">We recommend taking a selfie to help us find your headshots. If you are searching on behalf of a graduate, please skip.</span>
          <span v-else-if="eventTerminology">We recommend taking a selfie to help us find your event photos. If you are searching on behalf of someone else, please skip.</span>
          <span v-else>We recommend taking a selfie to help us find your special moment walking across the stage. If you are searching for photos on behalf of a graduate, please skip.</span>
        </p>
        <p class="mt-4 text-sm">
          <span v-if="isHeadshot && saveSelfieEnabled">We delete your selfie after searching unless you choose to save it on the next step.</span>
          <span v-else-if="saveSelfieEnabled">We delete your selfie after searching unless you choose to save it on the next step.</span>
          <span v-else>We delete your selfie after searching.</span>
        </p>
        <div class="mt-6 flex justify-between md:justify-start md:gap-3 ">
          <button @click="$emit('skip')" class="bg-red-500 text-white py-2 px-4 rounded hover:bg-red-700">
            Skip selfie
          </button>
          <button @click="controlSkipSelfieModal(false)" class="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700">
            <b>Take selfie</b>
          </button>
        </div>
      </div>
    </div>

    <div class="flex flex-col items-center">
      <div class="text-center mt-4 mx-auto max-w-7xl px-4 sm:mt-2 sm:px-6">
        <h2 class="text-2xl tracking-tight font-extrabold text-gray-900">
          {{ name }}
        </h2>
        <h2 class="text-xl font-bold text-gray-900 whitespace-nowrap">
          Take a selfie!
        </h2>
        <p class="mt-2 text-gray-700">
          To help find your images, take a selfie (or choose to skip).
        </p>
        <p class="my-2 text-gray-700" v-if="saveSelfieEnabled">
          We don't save this image unless you choose to save it on the next step.
        </p>
      </div>
      <div class="camera-container relative">
        <div v-if="isDevelopment" class="w-full text-center mb-4 p-4 bg-yellow-100 rounded">
          <p class="text-yellow-700 mb-2">Debug Mode: Upload an image</p>
          <input 
            type="file" 
            accept="image/*"
            @change="handleFileUpload"
            class="block mx-auto mb-2"
          >
          <p v-if="debugNotice" class="text-xs text-yellow-800">{{ debugNotice }}</p>
        </div>
        <!-- Status banners -->
        <div v-if="searchError" class="w-full text-center mb-3 p-3 bg-red-100 text-red-800 rounded">
          {{ searchError }}
        </div>
        <div v-else-if="searchNoResults" class="w-full text-center mb-3 p-3 bg-yellow-100 text-yellow-800 rounded">
          No matches found for that photo. Try retaking your selfie or skip to browse all photos.
        </div>
        <div v-if="autoSearch" class="w-full rounded-lg border border-gray-200 bg-white p-6 text-center">
          <div class="flex flex-col items-center justify-center text-gray-700">
            <svg class="animate-spin h-10 w-10 text-[#08697b] mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <div class="text-base font-medium">Finding your photos…</div>
            <div class="text-xs opacity-80 mt-1">Using your saved selfie</div>
          </div>
        </div>
        <div v-else class="media-area relative w-full" :style="{ aspectRatio: mediaAspect }">
          <!-- No blurred background; maintain consistent aspect ratio instead -->
          <video ref="video" v-show="!imageCaptured" autoplay playsinline class="absolute inset-0 w-full h-full object-contain"></video>
          <canvas ref="canvas" style="display: none"></canvas>
          <img v-if="imageCaptured" :src="capturedImage" alt="Captured selfie" class="absolute inset-0 w-full h-full object-contain" />
          <!-- Full overlay during search with tinted preview (uses object-contain to avoid rescale jumps) -->
          <div v-if="searching" class="absolute inset-0 rounded overflow-hidden z-20" role="status" aria-live="polite" aria-busy="true">
            <div class="absolute inset-0" style="background: linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55));"></div>
            <div class="relative z-10 flex flex-col items-center justify-center w-full h-full text-white">
              <svg class="animate-spin h-10 w-10 text-white mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              <div class="text-base font-medium">Finding your photos…</div>
              <div class="text-xs opacity-80 mt-1">This can take a few seconds</div>
            </div>
          </div>
        </div>
        <div v-if="!autoSearch" class="action-row">
          <button
            v-if="!imageCaptured"
            @click="controlSkipSelfieModal(true)"
            class="action-btn secondary bg-red-600 hover:bg-red-700"
          >
            <b>Skip selfie</b>
          </button>
          <button
            v-else
            @click="retake"
            class="action-btn secondary bg-red-600 hover:bg-red-700 disabled:opacity-60"
            :disabled="searching"
          >
            <b>Retake</b>
          </button>
          <button
            v-if="!imageCaptured"
            @click="captureSelfie"
            class="action-btn primary bg-green-600 hover:bg-green-700"
            :disabled="isSkipSelfieModalVisible"
          >
            <b>Capture Selfie</b>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { trackEvent } from '../../Analytics.vue';
import { getSnapshotConfig } from "../../snapshotConfig";
export default {
  name: "SearchByFace",
  props: {
    searching: { type: Boolean, default: false },
    searchError: { type: String, default: null },
    searchNoResults: { type: Boolean, default: false },
    name: { type: String, default: '' },
    saveSelfieEnabled: { type: Boolean, default: false },
    autoSearch: { type: Boolean, default: false },
  },
  data() {
    return {
      stream: null,
      imageCaptured: false,
      capturedImage: null,
      isSkipSelfieModalVisible: false,
      debugNotice: '',
      mediaAspect: '3 / 4',
    };
  },
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development';
    },
    snapshotConfig() {
      return getSnapshotConfig(this.$route.params.uni);
    },
    isHeadshot() {
      return this.snapshotConfig.isHeadshot;
    },
    eventTerminology() {
      return Boolean(this.snapshotConfig.eventTerminology) && !this.isHeadshot;
    },
  },
  mounted() {
    if (!this.autoSearch) {
      this.startCamera();
    }
  },
  beforeDestroy() {
    this.stopCamera();
  },
  watch: {
    autoSearch(newVal) {
      if (newVal) {
        this.stopCamera();
        return;
      }
      this.startCamera();
    },
  },
  methods: {
    setAspect(w, h) {
      if (!w || !h) return;
      const gw = Math.max(1, Math.round(w));
      const gh = Math.max(1, Math.round(h));
      this.mediaAspect = `${gw} / ${gh}`;
    },
    async startCamera() {
      if (this.autoSearch) return;
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });
        this.$refs.video.srcObject = this.stream;
        // Update container aspect once metadata is available
        this.$nextTick(() => {
          const v = this.$refs.video;
          if (!v) return;
          const setFromMeta = () => {
            if (v.videoWidth && v.videoHeight) this.setAspect(v.videoWidth, v.videoHeight);
          };
          v.addEventListener('loadedmetadata', setFromMeta, { once: true });
          // Fallback in case event fires before listener
          setTimeout(setFromMeta, 100);
        });
      } catch (error) {
        console.error("Error accessing camera:", error);
      }
    },
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },
    captureSelfie() {
      if (this.searching) return;
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      this.capturedImage = canvas.toDataURL("image/jpeg");
      this.imageCaptured = true;
      this.stopCamera();
      this.$emit('search-face', this.capturedImage);
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_CAPTURE_SELFIE",
      });
    },
    retake() {
      this.imageCaptured = false;
      this.capturedImage = null;
      this.startCamera();
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_RETAKE_SELFIE",
      });
    },
    controlSkipSelfieModal(bool){
      this.isSkipSelfieModalVisible = bool;
      if(bool === true){
        trackEvent(this.$route.path, {
          label: "SNAPSHOT_SKIP_SELFIE_MODAL_OPEN",
        });
      } else {
        trackEvent(this.$route.path, {
          label: "SNAPSHOT_SKIP_SELFIE_MODAL_CLOSED",
        });
      }
    },
    skipSelfie(){
      this.$emit('skip');
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_SKIP_SELFIE_CLICKED"
      });
    },
    compressImageForDebug(file, { maxBytes = 1500000, maxDim = 1280 } = {}) {
      return new Promise((resolve, reject) => {
        try {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = async () => {
            try {
              const originalWidth = img.naturalWidth || img.width;
              const originalHeight = img.naturalHeight || img.height;
              const originalBytes = file.size || 0;
              let targetW = originalWidth;
              let targetH = originalHeight;
              // Initial scale to fit max dimension
              if (Math.max(targetW, targetH) > maxDim) {
                const scale = maxDim / Math.max(targetW, targetH);
                targetW = Math.max(1, Math.round(targetW * scale));
                targetH = Math.max(1, Math.round(targetH * scale));
              }
              // Prepare canvas once; we will resize progressively if needed
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              let quality = 0.85;
              let attempts = 0;
              let dataUrl = '';
              let blob = null;
              while (attempts < 8) {
                canvas.width = targetW;
                canvas.height = targetH;
                ctx.clearRect(0, 0, targetW, targetH);
                ctx.drawImage(img, 0, 0, targetW, targetH);
                blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
                if (!blob) throw new Error('toBlob failed');
                if (blob.size <= maxBytes) break;
                // If too big, reduce quality until 0.6, then reduce dimensions
                if (quality > 0.6) {
                  quality = Math.max(0.6, quality - 0.1);
                } else {
                  // Reduce dimensions by 15% and try again
                  targetW = Math.max(320, Math.round(targetW * 0.85));
                  targetH = Math.max(320, Math.round(targetH * 0.85));
                }
                attempts += 1;
              }
              // Convert to DataURL
              dataUrl = await new Promise((res, rej) => {
                const fr = new FileReader();
                fr.onload = () => res(fr.result);
                fr.onerror = rej;
                fr.readAsDataURL(blob);
              });
              URL.revokeObjectURL(url);
              resolve({
                dataUrl,
                originalBytes,
                finalBytes: blob.size,
                originalWidth,
                originalHeight,
                finalWidth: targetW,
                finalHeight: targetH,
                quality,
                resized: (blob.size < originalBytes) || (targetW !== originalWidth || targetH !== originalHeight)
              });
            } catch (err) {
              URL.revokeObjectURL(url);
              reject(err);
            }
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Image load failed'));
          };
          img.src = url;
        } catch (e) {
          reject(e);
        }
      });
    },
    formatBytes(bytes) {
      if (!bytes || bytes <= 0) return '0 B';
      const units = ['B','KB','MB','GB'];
      const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
      const val = bytes / Math.pow(1024, i);
      return `${val.toFixed(val < 10 ? 1 : 0)} ${units[i]}`;
    },
    async handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      // Only shrink in debug mode to avoid hitting payload limits
      const MAX_BYTES = 1500000; // ~1.5MB target for base image (DataURL ~1.33x)
      const MAX_DIM = 1280;       // Max width/height for debug uploads
      this.debugNotice = '';
      try {
        const resized = await this.compressImageForDebug(file, { maxBytes: MAX_BYTES, maxDim: MAX_DIM });
        this.capturedImage = resized.dataUrl;
        // Keep the container aspect consistent with the chosen debug image
        this.setAspect(resized.finalWidth, resized.finalHeight);
        this.imageCaptured = true;
        this.stopCamera();
        this.$emit('search-face', this.capturedImage);
        if (resized.resized) {
          this.debugNotice = `Resized debug image from ${this.formatBytes(resized.originalBytes)} (${resized.originalWidth}x${resized.originalHeight}) to ${this.formatBytes(resized.finalBytes)} (${resized.finalWidth}x${resized.finalHeight}, q=${resized.quality.toFixed(2)}) to meet upload limits.`
        }
        trackEvent(this.$route.path, { label: "SNAPSHOT_DEBUG_UPLOAD_IMAGE" });
      } catch (e) {
        console.error('Failed to process debug image', e);
        // Fallback to raw if anything goes wrong
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.capturedImage = ev.target.result;
          this.imageCaptured = true;
          this.stopCamera();
          this.$emit('search-face', this.capturedImage);
        };
        reader.readAsDataURL(file);
      }
    }
  }
};
</script>

<style scoped>
.camera-container {
  display: flex;
  padding: 0 0;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.media-area {
  position: relative;
  width: 100%;
  border-radius: 8px;
  background: #000;    /* Neutral background behind object-contain */
  overflow: hidden;
}

video,
img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.action-row {
  position: sticky;
  bottom: 12px;
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
  padding: 8px 0 4px;
  background: linear-gradient(rgba(255, 255, 255, 0), rgba(247, 246, 242, 0.92));
  z-index: 20;
}

.action-btn {
  flex: 1 1 0;
  max-width: 180px;
}

button {
  padding: 8px 16px;
  border-radius: 4px;
  color: white;
  border: none;
  cursor: pointer;
}

.skip {
  padding: 8px 16px;
  border-radius: 4px;
  color: white;
  background-color: unset;
  border: none;
  cursor: pointer;
}
</style>
